import { readonly, ref, computed, getCurrentInstance } from 'vue'
import { appConfig, type AppConfig } from '@/config/app.config'

/**
 * Composable for accessing app configuration throughout the application.
 *
 * Provides read-only access to the centralized app configuration including
 * branding, SEO settings, and footer links.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useAppConfig } from '@/composables/useAppConfig'
 *
 * const { config, setPageTitle } = useAppConfig()
 * setPageTitle('Dashboard')
 * </script>
 *
 * <template>
 *   <h1>{{ config.name }}</h1>
 *   <p>{{ config.tagline }}</p>
 * </template>
 * ```
 */
export function useAppConfig() {
  /**
   * The app configuration object (read-only)
   */
  const config = readonly(appConfig) as Readonly<AppConfig>

  /**
   * Current page title (reactive)
   */
  const pageTitle = ref<string>('')

  /**
   * Full document title computed from page title and template
   */
  const documentTitle = computed(() => {
    if (!pageTitle.value) {
      return config.name
    }
    return config.seo.titleTemplate.replace('%s', pageTitle.value)
  })

  /**
   * Set the page title and update document.title
   * @param title - The page-specific title (will be combined with app name)
   */
  const setPageTitle = (title: string) => {
    pageTitle.value = title
    if (typeof document !== 'undefined') {
      document.title = documentTitle.value
    }
  }

  /**
   * Reset page title to just the app name
   */
  const resetPageTitle = () => {
    pageTitle.value = ''
    if (typeof document !== 'undefined') {
      document.title = config.name
    }
  }

  /**
   * Get meta tags for SEO (useful for SSR or meta tag injection)
   */
  const getMetaTags = () => {
    return {
      title: documentTitle.value,
      description: config.seo.defaultDescription,
      keywords: config.seo.keywords.join(', '),
    }
  }

  /**
   * Apply theme colors to CSS variables (if custom colors are configured)
   */
  const applyThemeColors = () => {
    if (typeof document === 'undefined') return

    const root = document.documentElement

    if (config.theme.primaryColor) {
      root.style.setProperty('--color-primary-500', config.theme.primaryColor)
    }

    if (config.theme.secondaryColor) {
      root.style.setProperty('--color-secondary-500', config.theme.secondaryColor)
    }
  }

  // Apply theme colors immediately if in browser (no lifecycle hook needed)
  // This is safe because it only modifies CSS variables
  const instance = getCurrentInstance()
  if (instance && typeof document !== 'undefined') {
    applyThemeColors()
  }

  return {
    /** Read-only app configuration */
    config,
    /** Current page title */
    pageTitle: readonly(pageTitle),
    /** Full document title (page title + app name) */
    documentTitle,
    /** Set the page title */
    setPageTitle,
    /** Reset page title to app name only */
    resetPageTitle,
    /** Get meta tags for SEO */
    getMetaTags,
    /** Apply custom theme colors to CSS variables */
    applyThemeColors,
  }
}
